/**
 * Integrations API service
 */

import { apiClient } from './client';

export interface Integration {
  id: string;
  organizationId: string;
  type: string;
  name: string;
  status: 'active' | 'inactive' | 'error';
  config?: Record<string, unknown>;
  connectedAt: string;
  lastSyncAt?: string;
}

export interface ConnectIntegrationRequest {
  type: string;
  config?: Record<string, string>;
}

export interface ResendConnectRequest {
  api_key: string;
  from_email: string;
  from_name: string;
}

export const integrationsApi = {
  list: async (organizationId: string): Promise<Integration[]> => {
    const response = await apiClient.get<Integration[]>(
      `/organizations/${organizationId}/integrations`
    );
    return response.data;
  },

  get: async (organizationId: string, integrationId: string): Promise<Integration> => {
    const response = await apiClient.get<Integration>(
      `/organizations/${organizationId}/integrations/${integrationId}`
    );
    return response.data;
  },

  getAuthUrl: async (organizationId: string, integrationType: string): Promise<string> => {
    const response = await apiClient.get<{ url: string }>(
      `/organizations/${organizationId}/integrations/${integrationType}/auth`
    );
    return response.data.url;
  },

  connect: async (
    organizationId: string,
    data: ConnectIntegrationRequest
  ): Promise<Integration> => {
    const response = await apiClient.post<Integration>(
      `/organizations/${organizationId}/integrations`,
      data
    );
    return response.data;
  },

  disconnect: async (organizationId: string, integrationId: string): Promise<void> => {
    await apiClient.delete(`/organizations/${organizationId}/integrations/${integrationId}`);
  },

  updateConfig: async (
    organizationId: string,
    integrationId: string,
    config: Record<string, unknown>
  ): Promise<Integration> => {
    const response = await apiClient.patch<Integration>(
      `/organizations/${organizationId}/integrations/${integrationId}`,
      { config }
    );
    return response.data;
  },

  sync: async (organizationId: string, integrationId: string): Promise<void> => {
    await apiClient.post(
      `/organizations/${organizationId}/integrations/${integrationId}/sync`
    );
  },

  testConnection: async (
    organizationId: string,
    integrationId: string
  ): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.post<{ success: boolean; message?: string }>(
      `/organizations/${organizationId}/integrations/${integrationId}/test`
    );
    return response.data;
  },

  connectResend: async (data: ResendConnectRequest): Promise<void> => {
    await apiClient.post('/integrations/resend/connect', data);
  },

  disconnectResend: async (): Promise<void> => {
    await apiClient.post('/integrations/resend/disconnect');
  },
};
